import { useCallback, useEffect, useMemo } from "react";
import {
  MenuItem,
  MenuSeparator,
  DataTable,
  DataTableAddRow,
  DataTableCellContent,
  DataTableCellTrigger,
  DataTableEmpty,
  EntityGroupAddSlot,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  createColumnHelper,
  renderDefaultGroupActions,
  Box,
  Text,
  Icon,
  type DataTableColumnDef,
  type EntityItemConfig,
  type EntityPoolConfig,
  type BulkAction,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
  type VisibilityState,
} from "@salestrak/ui";

import {
  EntityGroupTag,
  EntityTag,
  useEntityDetailOptional,
} from "#app/components/entity";
import { TeamAddMemberAction } from "#app/components/teams/team-add-member-action";
import { ENTITY_KIND_ICON, type EntityData } from "#app/lib/entity-visuals";
import { type FilterDef } from "#app/lib/hooks/use-table-filters";
import type { TableViewTableProps } from "#app/lib/hooks/use-table-view-settings";
import { type TeamRow, TeamsListDataDocument } from "#app/lib/data";
import { formatTeamHandle } from "#app/lib/team-handle";

// `FilterDef<EntityItemConfig>` — the pool item type travels through
// `useTableFilters` to `DataTableFilterButton`, which needs it to accept the
// app's `renderTableEntityFilter`. Left at the default `unknown`, the renderer
// does not typecheck and the entity rows silently render nothing.
export function buildTeamsFilterDefs(
  // The windowed member pool (`useUserPool`): its paging and server search
  // ride along, so the funnel finds every member, not only the first page.
  memberPool: EntityPoolConfig,
): FilterDef<EntityItemConfig>[] {
  const pools = [{ ...memberPool, label: "Members" }];
  return [
    { key: "leadIds", label: "Managers", icon: "user", type: "entity", pools, placeholder: "Search managers...", filterFormat: "idArray" },
    // rosterIds, not memberIds: "Members" here means ON the team — a manager
    // is a member too. The role-strict memberIds field stays in the schema
    // for consumers that want the split.
    { key: "rosterIds", label: "Members", icon: ENTITY_KIND_ICON.team, type: "entity", pools, placeholder: "Search members...", filterFormat: "idArray" },
    { key: "createdByIds", label: "Creator", icon: "user", type: "entity", pools, placeholder: "Search members...", filterFormat: "idArray" },
    // Maps through the generic date filter (`{ creationDate: { gte } }` off
    // the preset's day count) — the server's TeamWhereInput.creationDate.
    { key: "creationDate", label: "Created", icon: "calendar", type: "date" },
  ];
}
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { Omittable, SpacingToken } from "@salestrak/ui";
import { memberCountLabel } from "#app/lib/utils/member-count";

// The bulk bar's count-chip noun: "1 Team selected".
const TEAM_SELECTION_NOUN = { one: "Team", other: "Teams" };
// The settings page's one bulk verb is Delete, and the server gates that per
// team (manage.teams OR managing that team) — the same `viewerCanManage` the
// row menu honors — so a team the viewer can't manage gets no checkbox.
const canSelectTeam = (team: TeamRow) => team.viewerCanManage;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TeamsTableVariant = "app" | "settings";

/**
 * Shared by the table (sticky pinning), the column settings panel (locked
 * rows), and both parent pages. The panel ignores structural ids like
 * "select".
 */
export const TEAMS_PINNED_COLUMN_IDS = ["select", "teamName"];

/** Settings-variant columns hidden until the user shows them (persisted in
 *  `shown` by useTableViewSettings once they do). */
export const TEAMS_SETTINGS_DEFAULT_HIDDEN_COLUMN_IDS = [
  "visibility",
  "lastUpdateDate",
];

type TeamsTableProps = Omittable<{
  where: Record<string, unknown>;
  // Undefined when the list defers ordering to the API (`deferOrderBy`) and the
  // user has not sorted — the variable is then simply omitted.
  orderBy: Record<string, string> | undefined;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  /** True while any toolbar filter narrows the list — a zero-result filter
   *  shows no-match copy, never the create-team empty state. */
  filtered?: boolean;
  variant?: TeamsTableVariant;
  /**
   * Column definitions from `useTeamsColumns` — built by the parent page so
   * the `DataTableColumnSettings` panel in its header shares the same defs.
   */
  columns: DataTableColumnDef<TeamRow>[];
  /** Per-user order/hidden/width customization from `useTableViewSettings`. */
  tableViewProps?: TableViewTableProps;
  /** Scrollable top gutter under the host's toolbar band — pass `"md"` from a
   *  mount with a `DataTableToolbar` above the table; the settings page's
   *  `PageHeader` layout omits it. */
  gutterTop?: SpacingToken;
  /** Enables the actions column's built-in circle-plus column picker —
   *  pass `tableView.settingsProps.onColumnVisibilityChange`. */
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  memberPoolItems?: EntityItemConfig[];
  // Settings variant
  onNavigateToTeam?: (teamId: string) => void;
  onDeleteTeam?: (teamId: string, teamName: string) => void;
  onLeaveTeam?: (teamId: string) => void;
  /**
   * Opens the create-team flow from a "+ Create Team" row under the last team
   * — the reference add-row, the same affordance the issues table gives
   * "Add Project". Omit for a viewer who cannot create: the row is a promise
   * the dialog behind it would refuse, so the gate belongs at the mount that
   * already resolves it, beside the header button's own gate.
   */
  onCreateTeam?: () => void;
  // Bulk actions (settings variant)
  bulkActions?: BulkAction<TeamRow>[];
  /**
   * Reports the query's total row count (undefined until the first result).
   * Lets the parent swap chrome on a truly-empty list — e.g. hiding the
   * toolbar row when there are no teams at all.
   */
  onTotalChange?: (total: number | undefined) => void;
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getMemberCount(team: TeamRow): number {
  return team.leadCount + team.memberCount;
}

/**
 * The Members cell: the roster's first few faces, an overflow chip counting
 * the rest, and — where the cell is allowed to manage membership, for whoever
 * the server says may manage this team — the add slot that opens the same
 * picker the team's own Members view uses.
 *
 * The stack draws `rosterPreview` (four users, bounded server side) while the
 * "+N" counts against the true headcount, so a 40-person team shows faces and
 * an honest remainder without shipping 40 users per row.
 */
function TeamMembersCellValue({
  team,
  addSlot,
  interactive,
}: Omittable<{
  team: TeamRow;
  /** Whether the trailing add slot renders; the column decides it once so it
   *  can also tell the cell trigger it hosts a control of its own. */
  addSlot: boolean;
  /** False where the CELL owns the press (the door below): the avatars keep
   *  their hover previews and let the click through. */
  interactive: boolean;
}>) {
  const entities = useMemo<EntityData[]>(
    () =>
      team.rosterPreview.map((user) => ({
        kind: "user",
        id: user.userId,
        user,
      })),
    [team.rosterPreview],
  );

  // Every current teammate — the FULL roster, managers first, not the
  // four-face preview. It is both the "+N" card's answer to who is hidden and
  // the add picker's exclusion list, which has to be complete: `addMembers`
  // replaces an existing membership's role rather than refusing the
  // duplicate, so a manager the picker failed to exclude would be silently
  // demoted to member on pick.
  const rosterIds = useMemo(
    () => [...team.leadIds, ...team.memberIds],
    [team.leadIds, team.memberIds],
  );

  // The selector's trigger renders the tooltip's, which renders the slot —
  // one element carrying both behaviours, the nesting every picker-with-a-
  // tooltip in the app uses.
  const trailing = addSlot ? (
    <Tooltip>
      <TeamAddMemberAction
        teamId={team.teamId}
        existingUserIds={rosterIds}
        align="start"
        trigger={
          <TooltipTrigger
            render={<EntityGroupAddSlot size="xs" aria-label="Add member" />}
          />
        }
      />
      <TooltipPopup>Add member</TooltipPopup>
    </Tooltip>
  ) : undefined;

  return (
    <EntityGroupTag
      entities={entities}
      total={getMemberCount(team)}
      allIds={rosterIds}
      size="xs"
      max={4}
      title="Members"
      interactive={interactive}
      trailing={trailing}
    />
  );
}

const h = createColumnHelper<TeamRow>();

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.teams;

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

type UseTeamsColumnsParams = Omittable<{
  variant: TeamsTableVariant;
  onViewTeam?: (teamId: string) => void;
  onNavigateToTeam?: (teamId: string) => void;
  /** The Members cell as a door into the team page's own roster tab —
   *  membership is workspace-side, so settings leaves settings for it. */
  onViewTeamMembers?: (teamId: string) => void;
}>;

export function useTeamsColumns({
  variant,
  onViewTeam,
  onNavigateToTeam,
  onViewTeamMembers,
}: UseTeamsColumnsParams): DataTableColumnDef<TeamRow>[] {
  // The door takes the team page's own gate: a team outside the viewer's
  // workspace list has no page to land on, and the cell stays inert.
  const detail = useEntityDetailOptional();
  const canOpenTeam = detail?.canOpenEntity;
  return useMemo<DataTableColumnDef<TeamRow>[]>(
    () => {
      const handler = variant === "app" ? onViewTeam : onNavigateToTeam;

      const nameColumn = h.hoverAction({
        accessorKey: "teamName",
        header: "Name",
        enableSorting: true,
        meta: { width: 320 },
        // The headcount rides UNDER the name as the chip's own second line —
        // one identity, not a name beside a fact. The alias sits in its own
        // column: two muted facts trailing one name read as a sentence
        // nobody can scan, and only one of them sorts.
        //
        // No hover preview: the team card restates what this row already
        // shows (name, alias, visibility, headcount), so the cell's click is
        // the only affordance and the card never covers the row below.
        content: (team) => (
          <EntityTag
            entity={{ kind: "team", id: team.teamId, team }}
            description={memberCountLabel(getMemberCount(team))}
            padded
            interactive={false}
            preview={false}
          />
        ),
        onClickAction: (team) => handler?.(team.teamId),
        actionLabel: (team) => `View ${team.teamName}`,
      });

      // The handle wears its @ — it is the address a team is mentioned by,
      // so the column reads as "@field-crew", not as an opaque code.
      const aliasColumn = h.accessor({
        accessorKey: "teamKey",
        header: "Alias",
        enableSorting: true,
        meta: { width: 200, minWidth: 160 },
        cell: ({ row }) => (
          <DataTableCellContent>
            <Text size="sm" variant="muted">
              {formatTeamHandle(row.original.teamKey)}
            </Text>
          </DataTableCellContent>
        ),
      });

      // The viewer's own standing on the team. Every row says something: an
      // admin who is on no team still reads "Can manage" (manage.teams), and
      // anyone else reads "Not joined", never a blank cell.
      const accessColumn = h.display({
        id: "access",
        header: "Access",
        meta: { minWidth: 120 },
        cell: ({ row }) => {
          const team = row.original;
          const label =
            team.viewerRole === "lead"
              ? "Manager"
              : team.viewerRole === "member"
                ? "Joined"
                : team.viewerCanManage
                  ? "Can manage"
                  : "Not joined";

          return (
            <DataTableCellContent>
              <Text size="sm" variant="muted">{label}</Text>
            </DataTableCellContent>
          );
        },
      });

      // The roster's faces, not its size — the headcount already reads under
      // the team name, and a number beside the avatars says it twice.
      //
      // The cell is a door to the team page's Members tab wherever that page
      // is reachable, and wears the cell OUTLINE like every other pressable
      // cell: the ring marks a cell that acts on a press — an editor's picker
      // or, here, a door. Only a cell with no press at all stays bare, which
      // here means a team outside the viewer's list: its avatars then own
      // their own press (preview + profile) instead.
      //
      // Both variants carry the add slot, for whoever the server says may
      // manage the team: an admin scanning either list adds a member without
      // leaving it, while the roster itself still lives on the team page.
      // The slot is a press of its own inside the door's — so the door hosts
      // it as a nested control and yields the slot's press to the slot, while
      // the rest of the cell still routes.
      const membersColumn = h.display({
        id: "members",
        header: "Members",
        meta: { minWidth: 120 },
        cell: ({ row }) => {
          const team = row.original;
          const addSlot = team.viewerCanManage;
          const opensRoster =
            onViewTeamMembers !== undefined &&
            (canOpenTeam?.("team", team.teamId) ?? false);
          const stack = (
            <TeamMembersCellValue
              team={team}
              addSlot={addSlot}
              interactive={!opensRoster}
            />
          );

          if (!opensRoster) {
            return <DataTableCellContent>{stack}</DataTableCellContent>;
          }

          return (
            <DataTableCellTrigger
              nestedControls={addSlot}
              aria-label={`View ${team.teamName} members`}
              onClick={() => onViewTeamMembers(team.teamId)}
            >
              <DataTableCellContent>{stack}</DataTableCellContent>
            </DataTableCellTrigger>
          );
        },
      });

      const createdColumn = h.date({ accessorKey: "creationDate", header: "Created" });
      const updatedColumn = h.date({ accessorKey: "lastUpdateDate", header: "Updated" });

      // The hover preview's Visibility vocabulary (lock/globe + Private/
      // Public), as a quiet metadata cell on the Teammates-count grammar.
      const visibilityColumn = h.display({
        id: "visibility",
        header: "Visibility",
        meta: { minWidth: 100 },
        cell: ({ row }) => {
          const isPrivate = row.original.private;
          return (
            <DataTableCellContent>
              <Box direction="row" align="center" gap="xs">
                <Icon
                  name={isPrivate ? "lock" : "globe"}
                  size="sm"
                  color="muted"
                />
                <Text size="sm" variant="muted">
                  {isPrivate ? "Private" : "Public"}
                </Text>
              </Box>
            </DataTableCellContent>
          );
        },
      });

      if (variant === "app") {
        return [
          nameColumn,
          aliasColumn,
          accessColumn,
          membersColumn,
          createdColumn,
          updatedColumn,
        ];
      }

      // Settings variant. Visibility and Updated ship default-hidden (the
      // route passes TEAMS_SETTINGS_DEFAULT_HIDDEN_COLUMN_IDS) — reachable
      // from the column picker without widening the stock table.
      return [
        nameColumn,
        aliasColumn,
        membersColumn,
        visibilityColumn,
        createdColumn,
        updatedColumn,
      ];
    },
    [variant, onViewTeam, onNavigateToTeam, onViewTeamMembers, canOpenTeam]
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TeamsTable({
  where: whereProp,
  orderBy: orderByProp,
  sorting,
  onSortingChange,
  searchValue,
  filtered = false,
  variant = "app",
  columns,
  tableViewProps,
  gutterTop,
  onColumnVisibilityChange,
  onNavigateToTeam,
  onDeleteTeam,
  onLeaveTeam,
  onCreateTeam,
  bulkActions,
  onTotalChange,
}: TeamsTableProps) {
  // --- Group query state ---
  const { states, registerState, getGroupProps } = useGroupStates<TeamRow>();

  const handleRenderRowActions = useCallback(
    (team: TeamRow) => {
      if (variant === "app") {
        const isOnTeam = team.viewerRole != null;

        return (
          <>
            {/* Server-computed affordance: manage.teams holders AND this
                team's managers both pass viewerCanManage, matching the
                settings page's own gate. */}
            {onNavigateToTeam && team.viewerCanManage ? (
              <MenuItem onClick={() => onNavigateToTeam(team.teamId)}>
                <Icon name="settings" size="md" color="muted" />
                <Text size="sm">Team settings</Text>
              </MenuItem>
            ) : null}
            {isOnTeam ? (
              <>
                <MenuSeparator />
                <MenuItem onClick={() => onLeaveTeam?.(team.teamId)}>
                  <Icon name="log-out" size="md" color="muted" />
                  <Text size="sm">Leave team…</Text>
                </MenuItem>
              </>
            ) : null}
          </>
        );
      }

      // Settings variant
      return (
        <>
          <MenuItem onClick={() => onNavigateToTeam?.(team.teamId)}>
            <Icon name="settings" size="md" color="muted" />
            <Text size="sm">General</Text>
          </MenuItem>
          <MenuSeparator />
          <MenuItem onClick={() => onDeleteTeam?.(team.teamId, team.teamName)}>
            <Icon name="trash" size="md" color="muted" />
            <Text size="sm">Delete Team</Text>
          </MenuItem>
        </>
      );
    },
    [variant, onLeaveTeam, onNavigateToTeam, onDeleteTeam],
  );

  // "+ Create Team" as a real row under the last team. The list is one
  // ungrouped "__all__" group, so this renders once, at the foot of the
  // table. The truly-empty list keeps its own empty state instead — that
  // panel already carries the create invitation.
  const renderGroupFooter = useCallback(() => {
    if (!onCreateTeam) return null;
    return <DataTableAddRow label="Create Team" onClick={onCreateTeam} />;
  }, [onCreateTeam]);

  const allProps = getGroupProps("__all__");
  const allTotal = states.get("__all__")?.total;

  useEffect(() => {
    onTotalChange?.(allTotal);
  }, [allTotal, onTotalChange]);

  const groups = useMemo<DataTableGroupType<TeamRow>[]>(
    () => [
      {
        key: "__all__",
        label: "All Teams",
        totalCount: allTotal,
        ...allProps,
      },
    ],
    [allProps, allTotal],
  );

  return (
    <>
      {/* Ungrouped query provider */}
      <GroupQueryProvider<TeamRow>
        groupKey="__all__"
        document={TeamsListDataDocument}
        variables={{ where: whereProp, orderBy: orderByProp, limit: 50 }}
        getConnection={getConnection}
        onStateChange={registerState}
      />

      <Box direction="column" fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(r) => r.teamId}
          gutterTop={gutterTop}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          // The name cell stacks the headcount under the name — the 52px rung
          // gives that second line air the 44px row does not have.
          density="comfortable"
          pinnedColumnIds={TEAMS_PINNED_COLUMN_IDS}
          paddingX="lg"
          {...tableViewProps}
          onColumnVisibilityChange={onColumnVisibilityChange}
          groupActions={renderDefaultGroupActions}
          groupFooter={renderGroupFooter}
          renderRowActions={handleRenderRowActions}
          bulkActions={variant === "settings" ? bulkActions : undefined}
          bulkSelectionNoun={TEAM_SELECTION_NOUN}
          canSelectRow={variant === "settings" ? canSelectTeam : undefined}
          emptyState={
            searchValue || filtered ? (
              // A zero-result FILTER must not claim the workspace has no
              // teams — the create-team empty state is for the truly-empty
              // list only.
              <DataTableEmpty
                message={
                  searchValue
                    ? "No teams match your search"
                    : "No teams match your filters"
                }
              />
            ) : (
              <DataTableEmpty
                title="No teams yet"
                description="Create Teams to group Members."
                icon="users"
              />
            )
          }
        />
      </Box>
    </>
  );
}
